// ============================================================================
// Orders → Record a stock purchase. 2026-09-23; one step since 2026-10-02.
//
// A stock purchase is entered ONCE, when it has arrived and been paid
// (TCSD-0387), the way the invoice reads (cases and case price). Saving records
// the order and receives every line in the same step, so the stock is on the
// shelf at each line's location straight away — there is no order tracking and
// no separate receiving. Freight and tax are spread across the lines by line
// value into a landed cost per gallon or tab. The invoice date is kept because
// the learned lead time is arrival minus invoice date.
// A $0 case price is refused here and in the database: free or sample stock is
// a count, so the cost per tab stays what was paid (Ben 2026-10-02).
//
// ⛔ The request → approval → order flow for a single building is untouched:
// that is /supply/orders/new and it works exactly as before.
// ============================================================================

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../shared/components';
import { supabase } from '../../../shared/supabase';
import { SupplySubNav } from '../supply-sub-nav';
import { fetchStockOffices, recordPurchase, type PurchaseLineDraft } from './api';
import { fmtMoney } from './format';
import { useStockLoad } from './use-stock-data';
import { FIELD_CLASS, parseQty, saveErrorText } from './stock-window';

interface PricedItem {
  supply_id: string;
  item_name: string;
  vendor_sku: string | null;
  case_price: number | null;
  unit_price: number | null;
  pack: number | null;
  uom: string | null;
  category: string | null;
}

interface LineDraft {
  key: number;
  supply_id: string;
  office_id: string;
  cases: string;
  case_price: string;
}

// The local calendar day, not UTC: after 7 pm in Kansas City the UTC date is
// already tomorrow, and the database refuses an arrival in the future.
const today = () => new Date().toLocaleDateString('en-CA');

export default function StockPurchaseForm() {
  const navigate = useNavigate();
  const vendorsQ = useStockLoad(
    async () => {
      const { data, error } = await supabase
        .from('preferred_vendors')
        .select('vendor_id, vendor_name, primary_category')
        .eq('status', 'Active')
        .order('vendor_name');
      if (error) throw error;
      return (data ?? []) as Array<{ vendor_id: string; vendor_name: string | null; primary_category: string | null }>;
    },
    [],
    [] as Array<{ vendor_id: string; vendor_name: string | null; primary_category: string | null }>,
  );
  const officesQ = useStockLoad(() => fetchStockOffices(), [], [] as Awaited<ReturnType<typeof fetchStockOffices>>);

  const [vendorId, setVendorId] = useState('');
  const itemsQ = useStockLoad(
    async (): Promise<PricedItem[]> => {
      if (!vendorId) return [];
      const { data, error } = await supabase
        .from('vendor_supply_pricing')
        .select('supply_id, item_name, vendor_sku, case_price, unit_price, pack_size, uom')
        .eq('vendor_id', vendorId)
        .order('item_name');
      if (error) throw error;
      const rows = (data ?? []) as Array<{
        supply_id: string | null;
        item_name: string | null;
        vendor_sku: string | null;
        case_price: number | null;
        unit_price: number | null;
        pack_size: string | null;
        uom: string | null;
      }>;
      const ids = rows.map((r) => r.supply_id).filter((x): x is string => !!x);
      const { data: sup } = ids.length
        ? await supabase.from('supplies').select('supply_id, supply_category').in('supply_id', ids)
        : { data: [] };
      const cat: Record<string, string | null> = {};
      for (const s of (sup ?? []) as Array<{ supply_id: string; supply_category: string | null }>) cat[s.supply_id] = s.supply_category;
      return rows
        .filter((r) => r.supply_id)
        .map((r) => ({
          supply_id: r.supply_id!,
          item_name: r.item_name ?? r.supply_id!,
          vendor_sku: r.vendor_sku,
          case_price: r.case_price,
          unit_price: r.unit_price,
          pack: r.pack_size ? Number(String(r.pack_size).replace(/[^0-9.]/g, '')) || null : null,
          uom: r.uom,
          category: cat[r.supply_id!] ?? null,
        }));
    },
    [vendorId],
    [] as PricedItem[],
  );

  const [header, setHeader] = useState({
    po_date: today(),
    arrived_on: today(),
    invoice_number: '',
    shipping: '',
    tax: '',
    payment_method: '',
    notes: '',
  });
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextKey, setNextKey] = useState(1);

  const items = itemsQ.data;
  const itemFor = (id: string) => items.find((i) => i.supply_id === id);

  function addLine() {
    const first = items[0];
    setLines((ls) => [
      ...ls,
      {
        key: nextKey,
        supply_id: first?.supply_id ?? '',
        office_id: '',
        cases: '1',
        case_price: first ? String(first.case_price ?? (first.unit_price != null && first.pack ? first.unit_price * first.pack : '')) : '',
      },
    ]);
    setNextKey((k) => k + 1);
  }

  const parsed = lines.map((l) => ({
    ...l,
    n: parseQty(l.cases),
    p: parseQty(l.case_price),
    item: itemFor(l.supply_id),
  }));
  const subtotal = parsed.reduce((s, l) => s + (l.n ?? 0) * (l.p ?? 0), 0);
  const extras = (parseQty(header.shipping) ?? 0) + (parseQty(header.tax) ?? 0);
  const valid =
    !!vendorId &&
    !!header.po_date &&
    !!header.arrived_on &&
    header.arrived_on <= today() &&
    parsed.length > 0 &&
    parsed.every((l) => l.supply_id && l.office_id && l.n != null && l.n > 0 && l.p != null && l.p > 0 && l.item?.pack);

  const landed = useMemo(
    () =>
      parsed.map((l) => {
        const value = (l.n ?? 0) * (l.p ?? 0);
        const share = subtotal > 0 ? (extras * value) / subtotal : 0;
        const units = (l.n ?? 0) * (l.item?.pack ?? 0);
        return { key: l.key, units, perUnit: units > 0 ? (value + share) / units : null, isChem: l.item?.category === 'CHM' };
      }),
    [parsed, subtotal, extras],
  );

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      const payload: PurchaseLineDraft[] = parsed.map((l) => ({
        supply_id: l.supply_id,
        office_id: l.office_id,
        cases: l.n ?? 0,
        case_price: l.p ?? 0,
      }));
      const [po] = await recordPurchase(
        {
          vendor_id: vendorId,
          po_date: header.po_date,
          arrived_on: header.arrived_on,
          shipping: parseQty(header.shipping),
          tax: parseQty(header.tax),
          invoice_number: header.invoice_number || null,
          payment_method: header.payment_method || null,
          notes: header.notes || null,
        },
        payload,
      );
      navigate(`/supply/orders/${encodeURIComponent((po as { po_id: string }).po_id)}`);
    } catch (e) {
      // ⛔ The form stays filled in (TCSD-0044).
      setError(saveErrorText(e));
    } finally {
      setSaving(false);
    }
  }

  const officeOpts = officesQ.data;

  return (
    <div className="mx-auto max-w-5xl p-4 pb-28 sm:p-6">
      <SupplySubNav />
      <Link to="/supply/orders" className="mb-3 inline-flex items-center text-sm text-tcs-text-secondary hover:text-tcs-text">
        <ChevronLeft className="mr-1 h-4 w-4" />
        Back to orders
      </Link>
      <PageHeader
        title="Record a stock purchase"
        meta="Enter it once it has arrived and been paid, the way the invoice reads. Saving puts it straight on the shelf at each line's location."
      />

      {error && (
        <div role="alert" className="mt-4 rounded border border-st-danger-border-strong bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg-strong">
          {error}
        </div>
      )}

      <section className="mt-4 grid gap-3 rounded-xl border border-tcs-border bg-tcs-bg p-4 sm:grid-cols-2">
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-xs text-tcs-text-muted">Vendor</span>
          <select className={FIELD_CLASS} value={vendorId} onChange={(e) => { setVendorId(e.target.value); setLines([]); }}>
            <option value="">— pick a vendor —</option>
            {vendorsQ.data.map((v) => (
              <option key={v.vendor_id} value={v.vendor_id}>
                {v.vendor_name ?? v.vendor_id}
                {v.primary_category ? ` — ${v.primary_category}` : ''}
              </option>
            ))}
          </select>
        </label>
        <Field label="Invoice date" type="date" value={header.po_date} onChange={(v) => setHeader((h) => ({ ...h, po_date: v }))} />
        <Field
          label="Arrived on"
          type="date"
          max={today()}
          value={header.arrived_on}
          onChange={(v) => setHeader((h) => ({ ...h, arrived_on: v }))}
          hint="Today unless you change it. Invoice date to arrival is how the reorder lead time is learned."
        />
        <Field label="Invoice number" value={header.invoice_number} onChange={(v) => setHeader((h) => ({ ...h, invoice_number: v }))} />
        <Field label="Paid by" value={header.payment_method} onChange={(v) => setHeader((h) => ({ ...h, payment_method: v }))} placeholder="Card, Net 30…" />
      </section>

      <section className="mt-4 rounded-xl border border-tcs-border bg-tcs-bg p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-tcs-text">What was bought, and where each line ships</h3>
          <button
            type="button"
            disabled={!vendorId || items.length === 0}
            onClick={addLine}
            className="inline-flex items-center gap-1 rounded border border-tcs-border bg-tcs-raised px-2.5 py-1 text-xs disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" /> Add a line
          </button>
        </div>
        {vendorId && !itemsQ.loading && items.length === 0 && (
          <p className="mt-2 text-xs text-tcs-text-muted">This vendor has no priced products in the catalogue yet.</p>
        )}
        <ul className="mt-3 space-y-3">
          {parsed.map((l, i) => {
            const lc = landed[i];
            return (
              <li key={l.key} className="grid gap-2 rounded-lg border border-tcs-border bg-tcs-surface p-3 sm:grid-cols-[2fr_1.4fr_0.7fr_0.9fr_auto]">
                <label className="block">
                  <span className="mb-1 block text-xs text-tcs-text-muted">Product</span>
                  <select
                    className={FIELD_CLASS}
                    value={l.supply_id}
                    onChange={(e) => {
                      const it = itemFor(e.target.value);
                      setLines((ls) =>
                        ls.map((x) =>
                          x.key === l.key
                            ? { ...x, supply_id: e.target.value, case_price: String(it?.case_price ?? (it?.unit_price != null && it?.pack ? it.unit_price * it.pack : '')) }
                            : x,
                        ),
                      );
                    }}
                  >
                    {items.map((it) => (
                      <option key={it.supply_id} value={it.supply_id}>
                        {it.item_name}
                        {it.vendor_sku ? ` · item ${it.vendor_sku}` : ''}
                      </option>
                    ))}
                  </select>
                  <span className="mt-1 block text-[11px] text-tcs-text-muted">
                    {l.item?.pack ? `${l.item.pack} ${l.item.uom === 'TAB' ? 'tabs' : l.item.uom === 'GALLON' ? 'gallons' : 'each'} per case` : 'No pack size on file — cannot convert cases'}
                    {l.item && l.item.category !== 'CHM' ? ' · not a chemical: never counted in chemical cost' : ''}
                  </span>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs text-tcs-text-muted">Ship to stock location</span>
                  <select
                    className={FIELD_CLASS}
                    value={l.office_id}
                    onChange={(e) => setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, office_id: e.target.value } : x)))}
                  >
                    <option value="">— pick —</option>
                    {officeOpts.map((o) => (
                      <option key={o.office_id} value={o.office_id}>
                        {o.office_name}
                        {o.stock_role === 'receive_only' ? ' (receives freight)' : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <Field label="Cases" value={l.cases} onChange={(v) => setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, cases: v } : x)))} />
                <Field label="Case price" value={l.case_price} onChange={(v) => setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, case_price: v } : x)))} />
                <div className="flex items-end justify-between gap-2 sm:flex-col sm:items-end">
                  <span className="text-sm font-medium text-tcs-text">{fmtMoney((l.n ?? 0) * (l.p ?? 0))}</span>
                  <button
                    type="button"
                    aria-label="Remove line"
                    onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                    className="rounded border border-tcs-border p-1.5 text-tcs-text-secondary"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                {l.p === 0 && (
                  // Free or sample stock is a count, never a $0 line (Ben 2026-10-02). The database refuses it too.
                  <p role="alert" className="text-xs text-st-warn-fg sm:col-span-5">
                    A $0 line is not a purchase. Free or sample stock is a count: use “Correct the count here” on the Stock page,
                    or tell Ask AI “we got 499 free tabs at HQ”. That keeps the cost per tab at what was paid.
                  </p>
                )}
                {lc?.perUnit != null && (l.p ?? 0) > 0 && (
                  <p className="text-[11px] text-tcs-text-muted sm:col-span-5">
                    Adds {lc.units.toLocaleString('en-US')} {l.item?.uom === 'TAB' ? 'tabs' : l.item?.uom === 'GALLON' ? 'gallons' : 'each'} · landed cost{' '}
                    {fmtMoney(lc.perUnit, l.item?.uom === 'TAB' ? 3 : 2)} each after freight and tax.
                  </p>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <Field label="Freight" value={header.shipping} onChange={(v) => setHeader((h) => ({ ...h, shipping: v }))} placeholder="0.00" />
          <Field label="Tax" value={header.tax} onChange={(v) => setHeader((h) => ({ ...h, tax: v }))} placeholder="0.00" hint="Use the final invoice." />
          <div className="rounded-lg border border-tcs-border bg-tcs-surface p-3 text-sm">
            <div className="flex justify-between"><span className="text-tcs-text-muted">Lines</span><span>{fmtMoney(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-tcs-text-muted">Freight + tax</span><span>{fmtMoney(extras)}</span></div>
            <div className="mt-1 flex justify-between font-semibold"><span>Total</span><span>{fmtMoney(subtotal + extras)}</span></div>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-tcs-text-muted">Freight and tax are spread across the lines by line value.</p>
        <Field label="Notes (optional)" value={header.notes} onChange={(v) => setHeader((h) => ({ ...h, notes: v }))} />
      </section>

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          disabled={!valid || saving}
          onClick={() => submit()}
          className="rounded bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save — it’s here'}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  hint,
  max,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  hint?: string;
  max?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-tcs-text-muted">{label}</span>
      <input
        type={type}
        inputMode={type === 'text' && /price|Cases|Freight|Tax/.test(label) ? 'decimal' : undefined}
        className={FIELD_CLASS}
        value={value}
        max={max}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <span className="mt-1 block text-[11px] text-tcs-text-muted">{hint}</span>}
    </label>
  );
}
